// useRecorder.ts — foreground audio capture for the capture loop (CAP-1.x)
//
// Two capture backends behind ONE interface (RecorderResult { blob, mimeType, durationMs }), picked at
// runtime by platform so callers (Recorder / CaptureSheet) are untouched:
//
//   • NATIVE (Capacitor iOS/Android) — @independo/capacitor-voice-recorder. Records through the OS
//     recorder OFF the WebView, which sidesteps the Android System WebView getUserMedia
//     `NotReadableError` (a WebView platform limitation that reproduces after reboot / across
//     frameworks, not a bug in this hook). requestAudioRecordingPermission() -> startRecording() ->
//     stopRecording() hands back the take as base64 (audio/aac), which we decode to a Blob.
//     TRADE-OFFS on the 6.x plugin line (both resolve for free at the Capacitor-8 bump to the 8.x
//     plugin — same API, version-only change): the take is held as base64 IN MEMORY (no stream-to-disk
//     yet) so long recordings must be load-tested, and it is a SINGLE continuous session (no 1s
//     chunking) so a mid-recording crash loses the whole take.
//
//   • WEB (browser / desktop) — the original Web MediaRecorder path, unchanged:
//     navigator.mediaDevices.getUserMedia({ audio: true }) -> MediaRecorder -> Blob.
//
// TODO (build-host native): BACKGROUND / lock-screen recording is out of scope here — a long,
// lock-screen meeting recording needs a native foreground-service capture plugin (Android foreground
// service + iOS AVAudioSession background audio mode) added on the native build host.
//
// Permission: web getUserMedia triggers the WebView's runtime mic prompt; native routes the same
// consent through requestAudioRecordingPermission(). On Android the app ALSO needs the RECORD_AUDIO
// manifest permission (kept in AndroidManifest.xml) for either path to be grantable. Denial is
// surfaced via the `error` state rather than thrown.

import { useCallback, useEffect, useRef, useState } from 'react';
import { isNative } from '@/lib/native/platform';

export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopped' | 'error';

export interface RecorderResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export interface UseRecorder {
  status: RecorderStatus;
  /** Elapsed recording time in milliseconds (live while recording). */
  elapsedMs: number;
  /** The recorded blob once stopped, else null. */
  result: RecorderResult | null;
  /** Human-readable error (e.g. permission denied), else null. */
  error: string | null;
  start: () => Promise<void>;
  /** Stop and resolve with the final blob (also stored on `result`). */
  stop: () => Promise<RecorderResult | null>;
  /** Reset back to idle, discarding any captured blob. */
  reset: () => void;
}

// Prefer widely-supported, compact codecs. The first type MediaRecorder accepts wins; we fall back
// to the browser default (empty string) if none match.
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const t of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

// --- Native (capacitor-voice-recorder) helpers ---------------------------------------------------

// Decode the plugin's base64 payload into a typed-array-backed Blob. `atob` is available in the
// Capacitor WebView. Kept deliberately simple (a byte loop) — the whole take lives in memory on the
// 6.x plugin line, and this runs once at stop().
function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

// Map a voice-recorder plugin error to user-facing copy. The code arrives as the Error message on web
// (`new Error('CODE')`) and as the reject message on native (`call.reject(Messages.CODE)`); `.code` is
// a fallback for any Capacitor build that carries it there instead. "Device cannot record" uses a
// different code per platform (web DEVICE_CANNOT_VOICE_RECORD vs Android CANNOT_RECORD_ON_THIS_PHONE),
// so both map to one message.
function mapNativeRecorderError(err: unknown): string {
  const fromCode =
    err && typeof err === 'object' && 'code' in err ? (err as { code?: unknown }).code : undefined;
  const raw = fromCode || (err instanceof Error ? err.message : typeof err === 'string' ? err : '');
  const code = String(raw || '').toUpperCase();
  switch (code) {
    case 'MISSING_PERMISSION':
      return 'Microphone permission was denied. Enable it in Settings to record.';
    case 'MICROPHONE_BEING_USED':
      return 'The microphone is in use by another app. Close it and try again.';
    case 'DEVICE_CANNOT_VOICE_RECORD':
    case 'CANNOT_RECORD_ON_THIS_PHONE':
      return 'This device cannot record audio.';
    case 'EMPTY_RECORDING':
      return 'That recording was too short. Hold on a moment longer before stopping.';
    case 'FAILED_TO_RECORD':
    case 'FAILED_TO_FETCH_RECORDING':
      return 'Recording failed. Please try again.';
    default:
      return err instanceof Error && err.message ? err.message : 'Could not record audio.';
  }
}

export function useRecorder(): UseRecorder {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [result, setResult] = useState<RecorderResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  // True while a native plugin recording is in progress (native path only). Guards stop()/reset()/
  // unmount so the OS mic is released if the sheet closes without an explicit stop.
  const nativeRecordingRef = useRef(false);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Resolver for the in-flight stop() promise; fired from the recorder's onstop handler.
  const stopResolveRef = useRef<((r: RecorderResult | null) => void) | null>(null);

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const teardownStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setResult(null);
    chunksRef.current = [];
    setElapsedMs(0);

    // NATIVE path: record through the OS plugin (off the WebView). Same start/elapsed semantics as web
    // — flip to 'recording' and drive the 250ms elapsed tick; the actual bytes arrive at stop().
    if (isNative()) {
      setStatus('requesting');
      try {
        const { VoiceRecorder } = await import('@independo/capacitor-voice-recorder');
        const perm = await VoiceRecorder.requestAudioRecordingPermission();
        if (!perm.value) {
          setStatus('error');
          setError('Microphone permission was denied. Enable it in Settings to record.');
          return;
        }
        await VoiceRecorder.startRecording();
      } catch (err) {
        setStatus('error');
        setError(mapNativeRecorderError(err));
        return;
      }
      nativeRecordingRef.current = true;
      startedAtRef.current = Date.now();
      setStatus('recording');
      clearTick();
      tickRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 250);
      return;
    }

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === 'undefined'
    ) {
      setStatus('error');
      setError('Audio recording is not available on this device.');
      return;
    }

    setStatus('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // Permission denial (NotAllowedError) or no device (NotFoundError) land here.
      setStatus('error');
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError('Microphone permission was denied. Enable it in Settings to record.');
      } else if (name === 'NotFoundError') {
        setError('No microphone was found on this device.');
      } else {
        setError(err instanceof Error ? err.message : 'Could not start recording.');
      }
      return;
    }

    streamRef.current = stream;
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      teardownStream();
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Recorder could not be created.');
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      clearTick();
      const finalType = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: finalType });
      const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      const res: RecorderResult = { blob, mimeType: finalType, durationMs };
      setResult(res);
      setStatus('stopped');
      teardownStream();
      stopResolveRef.current?.(res);
      stopResolveRef.current = null;
    };
    recorder.onerror = () => {
      clearTick();
      teardownStream();
      setStatus('error');
      setError('Recording failed.');
      stopResolveRef.current?.(null);
      stopResolveRef.current = null;
    };

    startedAtRef.current = Date.now();
    // Timeslice so dataavailable fires periodically (more robust on long captures / abrupt teardown).
    recorder.start(1000);
    setStatus('recording');
    clearTick();
    tickRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);
  }, [clearTick, teardownStream]);

  const stop = useCallback(async (): Promise<RecorderResult | null> => {
    // NATIVE path: stop the plugin and assemble the RecorderResult from its base64 payload. Returns the
    // SAME { blob, mimeType, durationMs } shape as the web path so callers are identical.
    if (isNative()) {
      if (!nativeRecordingRef.current) return result;
      clearTick();
      try {
        const { VoiceRecorder } = await import('@independo/capacitor-voice-recorder');
        const { value } = await VoiceRecorder.stopRecording();
        nativeRecordingRef.current = false;
        const mimeType = value.mimeType || 'audio/aac';
        const blob = base64ToBlob(value.recordDataBase64, mimeType);
        const durationMs =
          typeof value.msDuration === 'number' && value.msDuration > 0
            ? value.msDuration
            : startedAtRef.current
              ? Date.now() - startedAtRef.current
              : 0;
        const res: RecorderResult = { blob, mimeType, durationMs };
        setResult(res);
        setStatus('stopped');
        return res;
      } catch (err) {
        nativeRecordingRef.current = false;
        setStatus('error');
        setError(mapNativeRecorderError(err));
        return null;
      }
    }

    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return result;
    }
    return new Promise<RecorderResult | null>((resolve) => {
      stopResolveRef.current = resolve;
      try {
        recorder.stop();
      } catch {
        clearTick();
        teardownStream();
        setStatus('error');
        resolve(null);
      }
    });
  }, [clearTick, result, teardownStream]);

  const reset = useCallback(() => {
    clearTick();
    // Native: release the OS mic if a plugin recording is still in progress (fire-and-forget; the
    // discarded take is intentional — reset() throws away any capture).
    if (nativeRecordingRef.current) {
      nativeRecordingRef.current = false;
      void import('@independo/capacitor-voice-recorder')
        .then(({ VoiceRecorder }) => VoiceRecorder.stopRecording())
        .catch(() => undefined);
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    teardownStream();
    recorderRef.current = null;
    chunksRef.current = [];
    stopResolveRef.current = null;
    setStatus('idle');
    setElapsedMs(0);
    setResult(null);
    setError(null);
  }, [clearTick, teardownStream]);

  // Clean up on unmount: stop any live stream/recorder so the mic indicator is released.
  useEffect(() => {
    return () => {
      clearTick();
      // Native: stop an in-progress plugin recording so the OS mic is released if the sheet unmounts
      // mid-capture (fire-and-forget).
      if (nativeRecordingRef.current) {
        nativeRecordingRef.current = false;
        void import('@independo/capacitor-voice-recorder')
          .then(({ VoiceRecorder }) => VoiceRecorder.stopRecording())
          .catch(() => undefined);
      }
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [clearTick]);

  return { status, elapsedMs, result, error, start, stop, reset };
}
